import { Component, Input, Output, EventEmitter, OnInit, Inject, AfterViewInit } from 'ng-metadata/core';
import * as angular from 'angular';
import './mini-modal.dialog.scss';

/**
 * @ngdoc directive
 * @name mwMiniModalDialog
 * @module mw.components.dialog
 *
 * @param {string} [cancel] - The text inside the cancel button of the modal.
 * @param {object} [data] - Data to input and have access to inside the modal. This can be an <code>array</code> or <code>object</code>.
 * @param {string} [ok] - The text inside the ok button of the modal.
 * @param {boolean} [no-padding=false] - Indicates if the modal should not have any padding.
 * @param {boolean} [no-title-separator=false] - Indicates if the modal title should have a visual separation between title and content.
 * @param {string} [title] - The text inside the heading of the modal. (NOW DEPRECATED PLEASE USE dialog-title instead)
 * @param {string} [dialog-title] - The text inside the heading of the modal.
 * @param {expression} [on-cancel] - Angular expression to evaluate when the cancel button is clicked in the modal.  $event is the ng-click event that triggered cancel.
 * @param {expression} [on-ok] - Angular expression to evaluate when the ok button is clicked in the modal. $event is the ng-click event that triggered ok.
 * @param {boolean} [is-ok-enabled=true] - Indicates if the OK button on the modal should enabled or disabled based on the flag.
 *
 * @description
 * `<mw-mini-modal-dialog>` is a component used to generate a small modal popup with optional capabilities to input
 *    and output data to and from. View the demo for implementation examples
 *
 * @usage
 * <hljs hljs-language="html">
 *   <mw-mini-modal-dialog class="mw-mini-modal-dialog" layout="column" data="$ctrl.items" title="Select Item" no-padding="true"
 *      ok="Save" cancel="Cancel" on-ok="$ctrl.handleOk($event)" on-cancel="$ctrl.handleCancel($event)">
 *      <mw-modal-header>
 *          <mw-modal-header-title>Select Items</mw-modal-header-title>
 *          <div flex=""></div>
 *      </mw-modal-header>
 *      <mw-modal-find>
 *          <mw-find search="$ctrl.searchModel"></mw-find>
 *      </mw-modal-find>
 *      <mw-modal-content class="mw-menu-dialog">
 *          <mw-menu-subheader>
 *              <span>RESULTS</span>
 *          </mw-menu-subheader>
 *          <mw-menu-item ng-repeat="item in $ctrl.items | filter:{name: $ctrl.searchModel}">
 *              {{item.name}}
 *          </mw-menu-item>
 *      </mw-modal-content>
 *   </mw-mini-modal-dialog>
 * </hljs>
 *
 */
@Component({
    selector: 'mw-mini-modal-dialog',
    template: `
        <md-toolbar class="mw-mini-modal__primary-toolbar" layout="column" ng-class="{'mw-mini-modal__primary-toolbar--no-separator':$ctrl.noTitleSeparator || $ctrl.isFindSlotFilled()}">
            <div class="md-toolbar-tools">
                <ng-transclude ng-transclude-slot="mwModalHeader" layout="row" flex="" class="mw-mini-modal__header-container">
                    <mw-modal-header-title mw-autotip>{{ $ctrl.title || $ctrl.dialogTitle}}</mw-modal-header-title>
                    <div flex=""></div>
                </ng-transclude>
            </div>
        </md-toolbar>
        <div ng-transclude="mwModalAlertMessage" layout-padding="layout-padding" class="md-caption mw-mini-dialog-alert"></div>
        <div ng-transclude="mwModalFind" class="mw-mini-dialog-find" ng-show="$ctrl.isFindSlotFilled()"></div>
        <md-dialog-content ng-transclude="mwModalContent" class="mw-dialog__content mw-mini-modal__content" ng-class="{'mw-mini-modal__content--no-padding':$ctrl.noPadding, 'mw-mini-modal__content--preserve-height': $ctrl.isFindSlotFilled()}" layout="column" layout-fill flex></md-dialog-content>
        <div ng-transclude="mwModalBanner" class="mw-mini-dialog-banner"></div>
        <md-dialog-actions class="mw-mini-modal__actions" layout="row">
            <ng-transclude ng-transclude-slot="mwModalFooter" layout="row" flex="">
                <div flex=""></div>
                <md-button ng-if="$ctrl.cancel" ng-click="$ctrl.handleCancel($event)" class="mw-mini-modal__cancel-button mw-button">
                    {{ $ctrl.cancel }}
                </md-button>
                <md-button ng-click="$ctrl.handleOk($event)" class="mw-mini-modal__ok-button mw-button" ng-disabled="!$ctrl.isOkEnabled">
                    {{ $ctrl.ok }}
                </md-button>
            </ng-transclude>
        </md-dialog-actions>
    `,
    legacy: {
        transclude: {
            'mwModalAlertMessage': '?mwModalAlertMessage',
            'mwModalContent': '?mwModalContent',
            'mwModalFind': '?mwModalFind',
            'mwModalBanner': '?mwModalBanner',
            'mwModalHeader': '?mwModalHeader',
            'mwModalFooter': '?mwModalFooter'
        }
    }
})
export class MiniModalDialogComponent implements OnInit, AfterViewInit {
    @Input('@') cancel: string;
    @Input('<') data: any;
    @Input('<') noPadding: boolean = false;
    @Input('<') noTitleSeparator: boolean = false;
    @Input('<') isOkEnabled: boolean;
    @Input('@') ok: string;
    @Input('@') title: string;
    @Input('@') dialogTitle: string;
    @Output() onCancel = new EventEmitter<ng.IAngularEvent>();
    @Output() onOk = new EventEmitter<ng.IAngularEvent>();

    constructor(
        @Inject('$transclude') private $transclude: ng.ITranscludeFunction,
        @Inject('$element') private $element,
        @Inject('$scope') private $scope) {
        'ngInject';
    }

    ngOnInit() {
        this.isOkEnabled = this.isOkEnabled !== false;
    }

    ngAfterViewInit() {
        if (this.$transclude.isSlotFilled('mwModalContent')) {
            const parent = this.$element.parent();
            const content = this.$element.find('md-dialog-content')[0];

            this.$scope.$watch( () => {
                return content.scrollHeight;
            }, (newValue, oldValue) => {
                parent.toggleClass('md-content-overflow', newValue > content.clientHeight);
            } );
        }
    }

    handleCancel(ngClickEvent) {
        this.onCancel.emit(ngClickEvent);
    }

    handleOk(ngClickEvent) {
        this.onOk.emit(ngClickEvent);
    }

    isFindSlotFilled() {
        return this.$transclude.isSlotFilled('mwModalFind');
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/dialog/mini-modal/mini-modal.dialog.component.ts